import * as React from "react";

import type { Stop } from "../types";
import { useStopLocations } from "./useStopLocations";
import { rootIdOf } from "../components/Map/transitMap/helpers";
import { STOP_LOCATIONS_HIDE_BELOW_ZOOM } from "../components/Map/transitMap/constants";

type Bounds = {
  north: number;
  east: number;
  south: number;
  west: number;
};

type Args = {
  stops: Stop[];
  bounds: Bounds | null;
  zoom: number;
  tpid: string;
};

type LocationState = {
  requestedTpid: string;
  resolvedTpid: string;
  locations: Record<string, Stop[]>;
};

export function useViewportStopLocations({ stops, bounds, zoom, tpid }: Args) {
  const { ensureLoaded, resolveTpid } = useStopLocations();

  const enabled = zoom >= STOP_LOCATIONS_HIDE_BELOW_ZOOM;

  const [state, setState] = React.useState<LocationState | null>(null);

  const previousRunRef = React.useRef<Promise<void>>(Promise.resolve());

  const visibleRootIds = React.useMemo(() => {
    if (!enabled || !bounds) return [];

    const latMargin = (bounds.north - bounds.south) * 0.2;
    const lngMargin = (bounds.east - bounds.west) * 0.2;

    const north = bounds.north + latMargin;
    const south = bounds.south - latMargin;
    const east = bounds.east + lngMargin;
    const west = bounds.west - lngMargin;

    const roots = new Set<string>();

    for (const stop of stops) {
      const lat = stop.parentLat ?? stop.lat;
      const lng = stop.parentLng ?? stop.lng;

      if (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat >= south &&
        lat <= north &&
        lng >= west &&
        lng <= east
      ) {
        roots.add(rootIdOf(stop.id));
      }
    }

    return Array.from(roots).sort();
  }, [stops, bounds, enabled]);

  const rootIdsKey = JSON.stringify(visibleRootIds);

  React.useEffect(() => {
    if (!enabled || rootIdsKey === "[]") return;

    let cancelled = false;

    const rootIds = JSON.parse(rootIdsKey) as string[];
    const previousRun = previousRunRef.current;

    const run = async () => {
      await previousRun;

      if (cancelled) return;

      const resolvedTpid = await resolveTpid(tpid);

      if (cancelled) return;

      setState((previous) => {
        if (
          previous?.requestedTpid === tpid &&
          previous.resolvedTpid === resolvedTpid
        ) {
          return previous;
        }

        return {
          requestedTpid: tpid,
          resolvedTpid,
          locations: {},
        };
      });

      let nextIndex = 0;

      const loadedLocations: Record<string, Stop[]> = {};

      const worker = async () => {
        while (!cancelled) {
          const index = nextIndex++;

          if (index >= rootIds.length) return;

          const rootId = rootIds[index];

          try {
            const locations = await ensureLoaded(rootId, resolvedTpid);

            if (cancelled) return;

            loadedLocations[rootId] = locations;
          } catch (error) {
            if (!cancelled) {
              console.error(
                `Kunde inte hämta lägen för hållplats ${rootId}`,
                error
              );
            }
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(4, rootIds.length) }, () => worker())
      );

      if (cancelled) return;

      setState((previous) => {
        if (
          !previous ||
          previous.requestedTpid !== tpid ||
          previous.resolvedTpid !== resolvedTpid
        ) {
          return previous;
        }

        const hasChanges = Object.entries(loadedLocations).some(
          ([rootId, locations]) => previous.locations[rootId] !== locations
        );

        if (!hasChanges) return previous;

        return {
          ...previous,
          locations: {
            ...previous.locations,
            ...loadedLocations,
          },
        };
      });

      await Promise.all(
        Array.from({ length: Math.min(4, rootIds.length) }, () => worker())
      );
    };

    const timer = window.setTimeout(() => {
      previousRunRef.current = run().catch((error: unknown) => {
        if (!cancelled) {
          console.error("Kunde inte hämta hållplatslägen", error);
        }
      });
    }, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [enabled, rootIdsKey, tpid, ensureLoaded, resolveTpid]);

  const locationsByRoot = React.useMemo(() => {
    const result: Record<string, Stop[]> = {};

    if (!enabled || state?.requestedTpid !== tpid) return result;

    for (const rootId of visibleRootIds) {
      const locations = state.locations[rootId];

      if (locations?.length) {
        result[rootId] = locations;
      }
    }

    return result;
  }, [enabled, state, tpid, visibleRootIds]);

  const displayedRoots = React.useMemo(
    () => new Set(Object.keys(locationsByRoot)),
    [locationsByRoot]
  );

  return {
    locationsByRoot,
    displayedRoots,
  };
}
